const numeroSeguro = (value) => {
  const numero = Number(value);
  return Number.isFinite(numero) ? numero : 0;
};

const etiquetaCampana = (activity) => activity.campaignId || 'Sin campaña';
const etiquetaCupon = (activity) => activity.couponCode || 'Sin cupón';
const etiquetaOrigen = (activity) => activity.origen || 'Sin origen';
const etiquetaTipo = (activity) => activity.tipo === 'prepagado' ? 'Prepagado' : 'Giftcard';

export const aggregateGiftcardSales = (activities = []) => {
  const created = activities.filter((activity) => (
    activity.eventType === 'created' && ['giftcard', 'prepagado'].includes(activity.tipo)
  ));
  const paid = created.filter((activity) => activity.clasificacionComercial !== 'promocional');
  const promotional = created.filter((activity) => activity.clasificacionComercial === 'promocional');
  const groups = new Map();

  created.forEach((activity) => {
    const campaign = etiquetaCampana(activity);
    const coupon = etiquetaCupon(activity);
    const origin = etiquetaOrigen(activity);
    const type = etiquetaTipo(activity);
    const key = `${campaign}\u0000${coupon}\u0000${origin}\u0000${type}`;
    const current = groups.get(key) || {
      key,
      campaign,
      coupon,
      origin,
      type,
      paidUnits: 0,
      promotionalUnits: 0,
      deliveredValue: 0,
      collectedAmount: 0,
      discountAmount: 0,
      incompleteRecords: 0,
    };
    const units = numeroSeguro(activity.cantidad) || 1;

    if (activity.clasificacionComercial === 'promocional') current.promotionalUnits += units;
    else current.paidUnits += units;

    if (activity.montosDisponibles !== true) current.incompleteRecords += 1;
    else {
      current.deliveredValue += numeroSeguro(activity.valorEntregado);
      current.collectedAmount += numeroSeguro(activity.montoCobrado);
      if (activity.clasificacionComercial !== 'promocional') {
        current.discountAmount += numeroSeguro(activity.montoDescuento);
      }
    }
    groups.set(key, current);
  });

  return {
    paidUnits: paid.reduce((sum, item) => sum + (numeroSeguro(item.cantidad) || 1), 0),
    promotionalUnits: promotional.reduce((sum, item) => sum + (numeroSeguro(item.cantidad) || 1), 0),
    giftcardUnits: paid.filter((item) => item.tipo === 'giftcard')
      .reduce((sum, item) => sum + (numeroSeguro(item.cantidad) || 1), 0),
    prepaidUnits: paid.filter((item) => item.tipo === 'prepagado')
      .reduce((sum, item) => sum + (numeroSeguro(item.cantidad) || 1), 0),
    deliveredValue: created.reduce((sum, item) => sum + numeroSeguro(item.valorEntregado), 0),
    collectedAmount: paid.reduce((sum, item) => sum + numeroSeguro(item.montoCobrado), 0),
    discountAmount: paid.reduce((sum, item) => sum + numeroSeguro(item.montoDescuento), 0),
    incompleteRecords: created.filter((item) => item.montosDisponibles !== true).length,
    groups: [...groups.values()].sort((a, b) => (
      b.collectedAmount - a.collectedAmount || b.paidUnits - a.paidUnits
    )),
  };
};

const santiagoFormatter = new Intl.DateTimeFormat('en-CA', {
  timeZone: 'America/Santiago', year: 'numeric', month: '2-digit', day: '2-digit',
  hour: '2-digit', minute: '2-digit', second: '2-digit', hourCycle: 'h23',
});

const santiagoMidnightToUtc = (dateKey) => {
  const [year, month, day] = dateKey.split('-').map(Number);
  const utcGuess = Date.UTC(year, month - 1, day);
  const parts = Object.fromEntries(
    santiagoFormatter.formatToParts(new Date(utcGuess)).map(({ type, value }) => [type, value])
  );
  const representedAsUtc = Date.UTC(
    Number(parts.year), Number(parts.month) - 1, Number(parts.day),
    Number(parts.hour), Number(parts.minute), Number(parts.second)
  );
  return new Date(utcGuess - (representedAsUtc - utcGuess));
};

const addCalendarDay = (dateKey) => {
  const [year, month, day] = dateKey.split('-').map(Number);
  const next = new Date(Date.UTC(year, month - 1, day + 1));
  return next.toISOString().slice(0, 10);
};

export const getSantiagoRange = (startDateKey, endDateKey) => ({
  start: santiagoMidnightToUtc(startDateKey),
  endExclusive: santiagoMidnightToUtc(addCalendarDay(endDateKey)),
});
